import React, { useEffect, useState, useMemo } from 'react';
import Modal from 'components/Tvendo/Common/ModalBase';
import { useStyleTabs } from 'hooks/TabPanel';
import moment from 'moment';
import { wsFindBy } from 'services/WService';
import { Box, List, ListItem, ListItemText } from '@material-ui/core';
import Button from 'components/CustomButtons/Button.js';
import { Info } from '@material-ui/icons';
import Checkbox from '@material-ui/core/Checkbox';
import { wsCreate } from 'services/WService';

export default function ConfigModal(props) {
  const classes = useStyleTabs();
  const Config = props.model;
  const { showNotify, reload } = props;
  const [loading, setLoading] = useState(false);
  const [configData, setConfigData] = useState([]);
  const [dataChanging, setDataChanging] = useState(Config);
  const [selectedDetail, setSelectedDetail] = useState(null);
  
  const [dataDetailMap, setDataDetailMap] = useState({});
  const [dataClient, setDataClient] = useState({});
  const [expandedClients, setExpandedClients] = useState({});

  const hasAnySelection = useMemo(() => {
    // Verificar si hay algún vehículo con rutas seleccionadas
    const hasVehicleSelection = Object.values(dataDetailMap).some(
      detailIds => detailIds && detailIds.length > 0
    );
    
    // Verificar si hay algún cliente seleccionado
    const hasClientSelection = Object.values(dataClient).some(
      clientIds => clientIds && clientIds.length > 0
    );
    
    return hasVehicleSelection || hasClientSelection;
  }, [dataDetailMap, dataClient]);

  function getVisitDayId() {
    const map = {0:7, 1:2, 2:3, 3:4, 4:5, 5:6, 6:7};
    return map[moment().day()];
  }

  async function ConfigList(){
    setLoading(true);
    let response = await wsFindBy('prog/config-w-details', {
      base_zone_id: Config.base_zone_id, 
      base_visit_day_id: getVisitDayId()
    });
    setLoading(false);
    
    if(response.success === false){
      return;
    }
    
    const data = response.data || [];
    setConfigData(data);

    // Inicializar selecciones por defecto: todos marcados
    const detailMap = {};
    const clientsMap = {};
    const expanded = {};
    
    data.forEach(item => {
      const ids = (item.details || []).map(d => d.id);
      detailMap[item.id] = ids;
      
      (item.details || []).forEach(d => {
        const orderIds = (d.orders || []).map((_, idx) => `${d.id}-o${idx}`);
        clientsMap[d.id] = orderIds;
        expanded[d.id] = false;
      });
    });
    
    setDataDetailMap(detailMap);
    setDataClient(clientsMap);
    setExpandedClients(expanded);
  }

  useEffect(() => {
    if (Config?.open) {
      ConfigList();
    }
  }, [Config?.open]);

  const handleShowDetails = (item) => {
    const isClosing = selectedDetail && selectedDetail.id === item.id;
    const newSelected = isClosing ? null : item;
    setSelectedDetail(newSelected);
  };

  const getClientsForDetail = (detail) => {
    return (detail.orders || []).map((order, idx) => ({
      id: `${detail.id}-o${idx}`,
      cardCode: order.CardCode,
      cardName: order.CardName,
      totalAmount: parseFloat(order.total_amount) || 0,
      totalOrder: parseInt(order.total_order) || 0,
    }));
  };

  const getTotalForDetail = (detail) => {
    const selectedClients = dataClient[detail.id] || [];
    const clients = getClientsForDetail(detail);
    const filtered = clients.filter(c => selectedClients.includes(c.id));
    return {
      totalAmount: filtered.reduce((sum, c) => sum + c.totalAmount, 0),
      totalClients: filtered.length,
      totalOrders: filtered.reduce((sum, c) => sum + c.totalOrder, 0),
    };
  };

  const getTotalForVehicle = (item) => {
    const selectedDetails = dataDetailMap[item.id] || [];
    const details = item.details || [];
    const filteredDetails = details.filter(d => selectedDetails.includes(d.id));
    
    let totalAmount = 0, totalClients = 0, totalOrders = 0;
    filteredDetails.forEach(detail => {
      const t = getTotalForDetail(detail);
      totalAmount += t.totalAmount;
      totalClients += t.totalClients;
      totalOrders += t.totalOrders;
    });
    
    return { totalAmount, totalClients, totalOrders };
  };

  const handleToggleVehicle = (item, checked) => {
    const allIds = (item.details || []).map(d => d.id);
    
    if (checked) {
      setDataDetailMap(prev => ({ ...prev, [item.id]: allIds }));
      setDataClient(prev => {
        const next = { ...prev };
        (item.details || []).forEach(d => { 
          next[d.id] = (d.orders || []).map((_, idx) => `${d.id}-o${idx}`);
        });
        return next;
      });
    } else {
      setDataDetailMap(prev => ({ ...prev, [item.id]: [] }));
      setDataClient(prev => {
        const next = { ...prev };
        (item.details || []).forEach(d => { next[d.id] = []; });
        return next;
      });
    }
  };

  const handleToggleDetail = (item, detail, checked) => {
    setDataDetailMap(prev => {
      const next = { ...(prev || {}) };
      const arr = new Set(next[item.id] || []);
      if (checked) arr.add(detail.id);
      else arr.delete(detail.id);
      next[item.id] = Array.from(arr);
      return next;
    });

    setDataClient(prev => {
      const next = { ...prev };
      if (checked) {
        next[detail.id] = (detail.orders || []).map((_, idx) => `${detail.id}-o${idx}`);
      } else {
        next[detail.id] = [];
      }
      return next;
    });
  };

  const handleToggleAllClients = (item, detail, clients, checked) => {
    setDataClient(prev => {
      const next = { ...prev };
      if (checked) {
        next[detail.id] = clients.map(c => c.id);
      } else {
        next[detail.id] = [];
      }
      return next;
    });
    
    const detailChecked = (dataDetailMap[item.id] || []).includes(detail.id);
    
    if (checked && !detailChecked) {
      setDataDetailMap(prev => ({ 
        ...prev, 
        [item.id]: Array.from(new Set([...(prev[item.id] || []), detail.id])) 
      }));
    } else if (!checked && detailChecked) {
      setDataDetailMap(prev => ({ 
        ...prev, 
        [item.id]: (prev[item.id] || []).filter(id => id !== detail.id) 
      }));
    }
  };

  const handleToggleClient = (item, detail, client, checked) => {
    setDataClient(prev => {
      const next = { ...prev };
      const arr = new Set(next[detail.id] || []);
      if (checked) arr.add(client.id);
      else arr.delete(client.id);
      next[detail.id] = Array.from(arr);
      return next;
    });

    if (!checked) {
      setTimeout(() => {
        setDataClient(prev => {
          const remain = prev[detail.id] || [];
          if (remain.length === 0) {
            setDataDetailMap(prevD => ({ 
              ...prevD, 
              [item.id]: (prevD[item.id] || []).filter(id => id !== detail.id) 
            }));
          }
          return prev;
        });
      }, 0);
    } else {
      setDataDetailMap(prevD => ({ 
        ...prevD, 
        [item.id]: Array.from(new Set([...(prevD[item.id] || []), detail.id])) 
      }));
    }
  };


  const buildPayload = () => {
    return configData
      .map(item => {
        const selectedDetails = dataDetailMap[item.id] || [];
        if (selectedDetails.length === 0) return null;

        return {
          base_zone_id: Config.base_zone_id,
          prog_vehicle_id: item.vehicle?.id,
          prog_distribution_staff_id_manager: item.manager?.id,
          prog_distribution_staff_id_inventory_manager: item.inventory_manager?.id,
          details: (item.details || [])
            .filter(d => selectedDetails.includes(d.id))
            .map(d => {
              const customers = (dataClient[d.id] || []).map(cid => {
                const idx = parseInt(cid.split('-o')[1]);
                const order = d.orders[idx];
                return { 
                  CardCode: order.CardCode,
                  CardName: order.CardName,
                  total_amount: order.total_amount,
                  total_order: order.total_order,
                  sale_order_id: order.sale_order_id
                };
              });

              if (customers.length === 0) return null;

              return {
                base_route_id: d.base_route_id,
                base_bureau_id: d.base_bureau_id,
                customers
              };
            })
            .filter(Boolean)
        };
      })
      .filter(Boolean);
  };


  return (
    <>
      <Modal
        title="Configuración de Programación"
        model={Config}
        setOpen={props.setOpen}
        showNotify={props.showNotify}
        reload={props.reload}
        hideSave={!hasAnySelection}
        onSubmit={async () => {
          const payload = buildPayload();

          if (payload.length === 0) {
            showNotify('Debe seleccionar al menos un registro', 'warning');
            return;
          }

          await wsCreate('prog/store-details', {
            configurations: payload
          });

          // showNotify('Configuración guardada correctamente');
          // reload();
        }}
      >
        <Box style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <List>
            {configData.map((item, idx) => {
              const vehicleTotal = getTotalForVehicle(item);
              const allDetailsSelected = (item.details || []).length > 0 && 
                (item.details || []).every(d => (dataDetailMap[item.id] || []).includes(d.id));
              const someDetailsSelected = (item.details || []).some(d => 
                (dataDetailMap[item.id] || []).includes(d.id));
              
              return (
                <React.Fragment key={item.id}>
                  <ListItem style={{ 
                    borderBottom: '1px solid #eee', 
                    paddingLeft: 0, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center' 
                  }}>
                    <Box display="flex" alignItems="center" flex={1}>
                      {idx + 1} 
                      <Checkbox
                        color="primary"
                        checked={allDetailsSelected}
                        indeterminate={someDetailsSelected && !allDetailsSelected}
                        onChange={(e) => handleToggleVehicle(item, e.target.checked)}
                      />
                      <ListItemText
                        primary={`${item.vehicle?.name || 'N/A'} - ${item.manager?.name || 'N/A'}`}
                        secondary={`Jefe de mercadería: ${item.inventory_manager?.name || 'N/A'} - Día: ${item.day?.description || 'N/A'} | Vehículo: ${item.vehicle?.code || 'N/A'} | Monto: ${vehicleTotal.totalAmount.toFixed(2)} | Clientes: ${vehicleTotal.totalClients} | Órdenes: ${vehicleTotal.totalOrders}`}
                        style={{ marginLeft: 8 }}
                      />
                    </Box>
                    <Box>
                      <Button
                        justIcon
                        round
                        simple
                        color={selectedDetail && selectedDetail.id === item.id ? 'primary' : 'info'}
                        onClick={() => handleShowDetails(item)}
                        title="Ver rutas y detalles"
                      >
                        <Info />
                      </Button>
                    </Box>
                  </ListItem>
                  
                  {selectedDetail && selectedDetail.id === item.id && (
                    <Box style={{ padding: '16px 24px 8px 24px', borderBottom: '1.3px solid #eee' }}>
                      {item.details && item.details.length > 0 ? (
                        <Box>
                          <Box display="flex" alignItems="center" style={{ marginBottom: 8 }}>
                            <Box minWidth={32} textAlign="center">#</Box>
                            <Box flex={1}><strong>Detalles (Rutas)</strong></Box>
                            <Box>
                              <Checkbox
                                color="primary"
                                checked={allDetailsSelected}
                                indeterminate={someDetailsSelected && !allDetailsSelected}
                                onChange={(e) => handleToggleVehicle(item, e.target.checked)}
                              />
                            </Box>
                          </Box>

                          {item.details.map((detail, i) => {
                            const clients = getClientsForDetail(detail);
                            const detailChecked = (dataDetailMap[item.id] || []).includes(detail.id);
                            const detailTotal = getTotalForDetail(detail);
                            const allClientsForDetailSelected = clients.length > 0 && 
                              clients.every(c => (dataClient[detail.id] || []).includes(c.id));
                            const someClientsSelected = clients.some(c => 
                              (dataClient[detail.id] || []).includes(c.id));
                            
                            return (
                              <Box key={detail.id} style={{ marginBottom: 8, padding: '8px 0' }}>
                                <Box display="flex" alignItems="center" style={{ borderBottom: '1.3px solid #eee' }}>
                                  <Box minWidth={32} textAlign="center">{i + 1}</Box>
                                  <Box flex={1}>
                                    <strong>Ruta:</strong> {detail.route?.code || '-'} - {detail.route?.description || '-'}
                                    {' | '}
                                    <strong>Mesa:</strong> {detail.bureau?.code || '-'} - {detail.bureau?.description || '-'}
                                    <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
                                      Monto: {detailTotal.totalAmount.toFixed(2)} | Clientes: {detailTotal.totalClients} | Órdenes: {detailTotal.totalOrders}
                                    </div>
                                  </Box>
                                  <Box>
                                    <Checkbox
                                      color="primary"
                                      checked={detailChecked}
                                      indeterminate={((dataClient[detail.id] || []).length > 0) && !detailChecked}
                                      onChange={(e) => handleToggleDetail(item, detail, e.target.checked)}
                                    />
                                  </Box>
                                  <Box>
                                    <Button
                                      justIcon
                                      round
                                      simple
                                      color="info"
                                      onClick={() => setExpandedClients(prev => ({ 
                                        ...prev, 
                                        [detail.id]: !prev[detail.id] 
                                      }))}
                                      title="Ver clientes"
                                    >
                                      <Info />
                                    </Button>
                                  </Box>
                                </Box>

                                {expandedClients[detail.id] && (
                                  <Box style={{ paddingLeft: 56, marginTop: 8, padding: '16px 24px 8px 24px' }}>
                                    <Box display="flex" alignItems="center" style={{ 
                                      marginBottom: 8, 
                                      paddingBottom: 8, 
                                      borderBottom: '1px solid #eee' 
                                    }}>
                                      <Box flex={1} style={{ fontSize: '12px' }}>
                                        <strong>Clientes de esta ruta</strong>
                                      </Box>
                                      <Checkbox
                                        color="primary"
                                        checked={allClientsForDetailSelected}
                                        indeterminate={someClientsSelected && !allClientsForDetailSelected}
                                        onChange={(e) => handleToggleAllClients(
                                          item, 
                                          detail, 
                                          clients, 
                                          e.target.checked
                                        )}
                                      />
                                    </Box>

                                    {clients.map((client, ci) => (
                                      <Box key={client.id} display="flex" alignItems="center" style={{ 
                                        marginBottom: 6, 
                                        borderBottom: '1.3px solid #eee' 
                                      }}>
                                        <Box flex={1}>
                                          {ci + 1}. Código: {client.cardCode} - Cliente: {client.cardName}
                                          <div style={{ fontSize: '11px', color: '#666', marginTop: 2 }}>
                                            Monto: {client.totalAmount.toFixed(2)} | Órdenes: {client.totalOrder}
                                          </div>
                                        </Box>
                                        <Box>
                                          <Checkbox
                                            color="primary"
                                            checked={(dataClient[detail.id] || []).includes(client.id)}
                                            onChange={(e) => handleToggleClient(
                                              item, 
                                              detail, 
                                              client, 
                                              e.target.checked
                                            )}
                                          />
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      ) : (
                        <Box style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                          No hay detalles de rutas disponibles
                        </Box>
                      )}
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </List>
          {configData.length === 0 && (
            <Box style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
              No hay configuraciones disponibles
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
}